import React, { createContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useProfile } from "../ProfileProvider/useProfile";
import { WorkSpaceUpdate } from "../../model/WorkSpaceModel";
import { GetOneWorkSpace, GetOneWorkSpaceFolderInformation } from "../../services/WorkSpaceServices";
import { FolderInformation, WorkSpaceUserName } from "../../model/ItemModel";
import { GetOneItem } from "../../services/Item/ItemServicesGet";
import { KeydrivePlan } from "../../model/UserModel";
import useExtractPlan from "../../hooks/useExtractPlan";

interface RouteContextProps {
    pathname: string;
    isInAdminArea: boolean;
    isSystemAdmin: boolean;
    isOrgAdmin: boolean;
    isWorkspaceAdmin: boolean;
}

interface IPermissionRequiredItem {
    adminSystem: boolean; 
    adminOrg: boolean;
    adminWorkspace: boolean;
    privilegeRequired: boolean;
}

interface IPermissionsRequired {
    permissionRequired: IPermissionRequiredItem;
    path: string;
}

interface IRequiredPermission {
    adminSystem: boolean;
    adminOrg: boolean;
    adminWorkspace: boolean;
}

interface IRoutesBlocked {
    path: string;
    permissionRequired: IRequiredPermission;
    plansRequired: KeydrivePlan[];
}

export const RouteContext = createContext<RouteContextProps | undefined>(undefined);

export function RouteProvider({ children }: Readonly<{ children: React.ReactNode }>) {
    const location = useLocation();
    const navigate = useNavigate();
    const profile = useProfile();
    const {keydrivePlan} = useExtractPlan();

    const [isSystemAdmin, setIsSystemAdmin] = useState(false);
    const [isOrgAdmin, setIsOrgAdmin] = useState(false);
    const [isWorkspaceAdmin, setIsWorkspaceAdmin] = useState(false);

    console.log(keydrivePlan)

    const routesBlocked: IRoutesBlocked[] = [
        {
            path: "adminconsole",
            permissionRequired: { adminSystem: true, adminOrg: true, adminWorkspace: false },
            plansRequired: []
        },
        {
            path: "permission",
            permissionRequired: { adminSystem: true, adminOrg: true, adminWorkspace: true },
            plansRequired: [
                KeydrivePlan.ESSENCIAL,
                KeydrivePlan.PROFISSIONAL,
                KeydrivePlan.PERSONAL,
                KeydrivePlan.SCALE,
            ]
        },
        {
            path: "validationrules",
            permissionRequired: { adminSystem: false, adminOrg: false, adminWorkspace: false },
            plansRequired: [
                KeydrivePlan.ESSENCIAL,
                KeydrivePlan.PROFISSIONAL,
                KeydrivePlan.PERSONAL,
                KeydrivePlan.SCALE,
            ]
        },
        {
            path: "temporality",
            permissionRequired: { adminSystem: false, adminOrg: false, adminWorkspace: false },
            plansRequired: [
                KeydrivePlan.ESSENCIAL,
                KeydrivePlan.PROFISSIONAL,
                KeydrivePlan.PERSONAL,
                KeydrivePlan.SCALE,
            ]
        },
        {
            path: "recyclebin",
            permissionRequired: { adminSystem: false, adminOrg: false, adminWorkspace: false },
            plansRequired: [
                KeydrivePlan.ESSENCIAL,
                KeydrivePlan.PROFISSIONAL,
                KeydrivePlan.PERSONAL,
                KeydrivePlan.SCALE,
            ]
        },
        {
            path: "pendencies",
            permissionRequired: { adminSystem: false, adminOrg: false, adminWorkspace: false },
            plansRequired: [
                KeydrivePlan.ESSENCIAL,
                KeydrivePlan.PROFISSIONAL,
                KeydrivePlan.PERSONAL,
                KeydrivePlan.SCALE,
            ]
        },
        {
            path: "privileges",
            permissionRequired: { adminSystem: false, adminOrg: false, adminWorkspace: false },
            plansRequired: [
                KeydrivePlan.ESSENCIAL,
                KeydrivePlan.PROFISSIONAL,
                KeydrivePlan.PERSONAL,
                KeydrivePlan.SCALE,
            ]
        },
    ];

    useEffect(() => {
        if (!profile) {
            setIsSystemAdmin(false);
            setIsOrgAdmin(false);
            setIsWorkspaceAdmin(false);
            return;
        }

        let orgAdmin = false;
        let workspaceAdmin = false;

        if (profile.organizations) {
            const orgPath = location.pathname.split("/")[1];
            for (const organization of profile.organizations) {
                if (orgPath === organization.internalName) {
                    if (organization.isAdmin) orgAdmin = true;
                    if (organization.isAdminWorkspace) workspaceAdmin = true;
                    break;
                }
            }
        }

        setIsSystemAdmin(!!profile.adminSystem);
        setIsOrgAdmin(orgAdmin);
        setIsWorkspaceAdmin(workspaceAdmin);
    }, [profile, location.pathname]);

    useEffect(() => {
        if (!profile?.id) return;

        const currentPath = location.pathname.toLowerCase();

        const blockedRoute = routesBlocked.find((element) => 
            currentPath.includes(element.path)
        );

        if (!blockedRoute) return;

        let needBlockByPlan = true;
        if (blockedRoute.plansRequired.length > 0) {
            if (keydrivePlan != KeydrivePlan.FREE) {
                needBlockByPlan = false;
            }
        }

        const hasAnyPermissionRequired = 
            blockedRoute.permissionRequired.adminSystem ||
            blockedRoute.permissionRequired.adminOrg ||
            blockedRoute.permissionRequired.adminWorkspace;

        const hasPermission = hasAnyPermissionRequired ? (
            (blockedRoute.permissionRequired.adminSystem && isSystemAdmin) ||
            (blockedRoute.permissionRequired.adminOrg && isOrgAdmin) ||
            (blockedRoute.permissionRequired.adminWorkspace && isWorkspaceAdmin)
        ) : true;
    }, [
        location.pathname,
        isSystemAdmin,
        isOrgAdmin,
        isWorkspaceAdmin,
        keydrivePlan,
        profile?.id,
        navigate,
        routesBlocked
    ]);

    const data = useMemo<RouteContextProps>(
        () => ({
            pathname: location.pathname,
            isInAdminArea: location.pathname.toLowerCase().includes("/adminconsole"),
            isSystemAdmin,
            isOrgAdmin,
            isWorkspaceAdmin
        }),
        [location.pathname, isSystemAdmin, isOrgAdmin, isWorkspaceAdmin]
    );

    return <RouteContext.Provider value={data}>{children}</RouteContext.Provider>;
}